/**
 * Hugging Face API Service
 *
 * PRODUCTION VERSION (2025-12-29): Integration with HuggingFace Inference API
 *
 * Features:
 * - Text generation with various models
 * - Conversational AI
 * - Text-to-Speech
 * - Speech-to-Text
 *
 * Models Used:
 * - mistralai/Mixtral-8x7B-Instruct-v0.1 (conversation)
 * - microsoft/DialoGPT-large (casual chat)
 * - facebook/blenderbot-400M-distill (friendly conversation)
 *
 * Requires: HF_API_TOKEN environment variable or localStorage
 */

const HF_API_URL = 'https://api-inference.huggingface.co/models';

// Get API token from various sources
const getApiToken = () => {
  // Try localStorage first (for frontend storage)
  const localToken = localStorage.getItem('HF_API_TOKEN');
  if (localToken) return localToken;

  // Try from environment via API
  return null;
};

/**
 * Query Hugging Face Inference API
 */
const queryHuggingFace = async (model, payload, options = {}) => {
  const token = getApiToken();

  if (!token) {
    console.warn('Hugging Face API token not found. Using fallback responses.');
    return null;
  }

  try {
    const response = await fetch(`${HF_API_URL}/${model}`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || `HTTP ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Hugging Face API error:', error);
    throw error;
  }
};

/**
 * Generate conversational response using Mixtral
 */
export const generateConversationalResponse = async (messages, systemPrompt = null) => {
  const model = 'mistralai/Mixtral-8x7B-Instruct-v0.1';

  // Build prompt with conversation history
  let prompt = systemPrompt || `You are ASTRA, an intelligent AI trading assistant. You are friendly, knowledgeable about financial markets, and can engage in casual conversation while also providing expert trading advice. Keep responses concise but helpful.`;

  prompt += '\n\n';
  messages.forEach(msg => {
    prompt += `${msg.role === 'user' ? 'User' : 'ASTRA'}: ${msg.content}\n`;
  });
  prompt += 'ASTRA:';

  const result = await queryHuggingFace(model, {
    inputs: prompt,
    parameters: {
      max_new_tokens: 200,
      temperature: 0.7,
      top_p: 0.9,
      repetition_penalty: 1.1,
      return_full_text: false
    }
  });

  if (result && result[0]?.generated_text) {
    // Clean up the response
    let text = result[0].generated_text.trim();
    // Remove any continuation of the conversation format
    const nextUserIndex = text.indexOf('\nUser:');
    if (nextUserIndex > 0) {
      text = text.substring(0, nextUserIndex);
    }
    return text;
  }

  return null;
};

/**
 * Generate casual chat response using BlenderBot
 */
export const generateCasualResponse = async (userMessage, conversationHistory = []) => {
  const model = 'facebook/blenderbot-400M-distill';

  // BlenderBot expects past_user_inputs and generated_responses
  const past_user_inputs = conversationHistory
    .filter(m => m.role === 'user')
    .map(m => m.content);

  const generated_responses = conversationHistory
    .filter(m => m.role === 'assistant')
    .map(m => m.content);

  const result = await queryHuggingFace(model, {
    inputs: {
      past_user_inputs,
      generated_responses,
      text: userMessage
    }
  });

  return result?.generated_text || null;
};

/**
 * Generate financial-focused response
 */
export const generateFinancialResponse = async (query, context = {}) => {
  const model = 'mistralai/Mixtral-8x7B-Instruct-v0.1';

  let contextInfo = '';
  if (context.portfolio) {
    contextInfo = `\nPortfolio Context: Total value: ${context.portfolio.totalValue}, Positions: ${context.portfolio.positionCount}`;
  }
  if (context.sentiment) {
    contextInfo += `\nMarket Sentiment: ${context.sentiment.description || 'Neutral'}`;
  }

  const prompt = `You are ASTRA, an expert AI trading assistant. Provide accurate, helpful financial advice.
${contextInfo}

User Query: ${query}

Provide a concise, informative response:`;

  const result = await queryHuggingFace(model, {
    inputs: prompt,
    parameters: {
      max_new_tokens: 250,
      temperature: 0.5, // Lower for more factual responses
      top_p: 0.85
    }
  });

  return result?.[0]?.generated_text?.trim() || null;
};

/**
 * Text-to-Speech using Hugging Face (returns audio blob)
 */
export const textToSpeech = async (text) => {
  const token = getApiToken();
  if (!token) return null;

  const model = 'espnet/kan-bayashi_ljspeech_vits';

  try {
    const response = await fetch(`${HF_API_URL}/${model}`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ inputs: text }),
    });

    if (!response.ok) throw new Error(`TTS failed: ${response.status}`);

    const audioBlob = await response.blob();
    return URL.createObjectURL(audioBlob);
  } catch (error) {
    console.error('TTS error:', error);
    return null;
  }
};

/**
 * Speech-to-Text using Hugging Face Whisper
 */
export const speechToText = async (audioBlob) => {
  const token = getApiToken();
  if (!token) return null;

  const model = 'openai/whisper-large-v3';

  try {
    const response = await fetch(`${HF_API_URL}/${model}`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: audioBlob,
    });

    if (!response.ok) throw new Error(`STT failed: ${response.status}`);

    const result = await response.json();
    return result.text;
  } catch (error) {
    console.error('STT error:', error);
    return null;
  }
};

/**
 * Set API token (for settings page)
 */
export const setApiToken = (token) => {
  if (token) {
    localStorage.setItem('HF_API_TOKEN', token);
  } else {
    localStorage.removeItem('HF_API_TOKEN');
  }
};

/**
 * Check if API token is configured
 */
export const hasApiToken = () => {
  return !!getApiToken();
};

/**
 * Test API connection
 */
export const testConnection = async () => {
  try {
    const result = await queryHuggingFace('gpt2', {
      inputs: 'Hello',
      parameters: { max_new_tokens: 5 }
    });
    return { success: true, message: 'Connected to Hugging Face API' };
  } catch (error) {
    return { success: false, message: error.message };
  }
};

export default {
  generateConversationalResponse,
  generateCasualResponse,
  generateFinancialResponse,
  textToSpeech,
  speechToText,
  setApiToken,
  hasApiToken,
  testConnection
};
